// Accounting & Financial Management Service Layer
// VAT, Chart of Accounts, P&L, Cash Flow, Audit Trail, Tax Compliance
import * as Sentry from '@sentry/react'
import { restSet, restDelete, restQuery, restGet } from './firestoreRest'
import { getPosSales, getOverheads, getPurchases, getInventoryItems, getAllPosProducts, getProductCostCard, getWastageLog, getBranch } from './posStore'

// ────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────

export const VAT_RATE = 0.15 // 15% SA VAT
export const TRADING_DAYS_PER_MONTH = 26

// ────────────────────────────────────────────
// Chart of Accounts (simplified for food business)
// ────────────────────────────────────────────

export const CHART_OF_ACCOUNTS = {
  // Revenue (4xxx)
  4000: { code: '4000', name: 'Sales Revenue', type: 'revenue', category: 'income' },
  4010: { code: '4010', name: 'Kitchen Sales', type: 'revenue', category: 'income' },
  4020: { code: '4020', name: 'Bakery Sales', type: 'revenue', category: 'income' },
  4030: { code: '4030', name: 'Beverage Sales', type: 'revenue', category: 'income' },
  4040: { code: '4040', name: 'Miscellaneous Sales', type: 'revenue', category: 'income' },
  4090: { code: '4090', name: 'Sales Returns & Voids', type: 'revenue', category: 'contra' },

  // Cost of Sales (5xxx)
  5000: { code: '5000', name: 'Cost of Goods Sold', type: 'expense', category: 'cogs' },
  5010: { code: '5010', name: 'Raw Materials — Meat', type: 'expense', category: 'cogs' },
  5020: { code: '5020', name: 'Raw Materials — Bakery', type: 'expense', category: 'cogs' },
  5030: { code: '5030', name: 'Raw Materials — Groceries', type: 'expense', category: 'cogs' },
  5040: { code: '5040', name: 'Raw Materials — Beverages', type: 'expense', category: 'cogs' },
  5050: { code: '5050', name: 'Packaging Materials', type: 'expense', category: 'cogs' },
  5060: { code: '5060', name: 'Wastage & Spoilage', type: 'expense', category: 'cogs' },

  // Operating Expenses (6xxx)
  6000: { code: '6000', name: 'Operating Expenses', type: 'expense', category: 'opex' },
  6010: { code: '6010', name: 'Rent & Rates', type: 'expense', category: 'opex' },
  6020: { code: '6020', name: 'Electricity & Utilities', type: 'expense', category: 'opex' },
  6030: { code: '6030', name: 'Staff Wages & Salaries', type: 'expense', category: 'opex' },
  6040: { code: '6040', name: 'Gas & Fuel', type: 'expense', category: 'opex' },
  6050: { code: '6050', name: 'Equipment Maintenance', type: 'expense', category: 'opex' },
  6060: { code: '6060', name: 'Internet & Communications', type: 'expense', category: 'opex' },
  6070: { code: '6070', name: 'Cleaning & Hygiene', type: 'expense', category: 'opex' },
  6080: { code: '6080', name: 'Insurance', type: 'expense', category: 'opex' },
  6090: { code: '6090', name: 'Depreciation', type: 'expense', category: 'opex' },
  6100: { code: '6100', name: 'Bank Charges', type: 'expense', category: 'opex' },
  6110: { code: '6110', name: 'Marketing & Advertising', type: 'expense', category: 'opex' },
}

// Map overhead names to GL codes
const OVERHEAD_GL_MAP = {
  'Rent': '6010', 'Electricity': '6020', 'Staff Wages': '6030',
  'Gas / Fuel': '6040', 'Packaging': '5050', 'Equipment Maintenance': '6050',
  'Internet': '6060', 'Cleaning': '6070', 'Insurance': '6080',
}

// Map product departments to revenue GL codes
const DEPT_REVENUE_MAP = {
  kitchen: '4010', sandwich: '4010', bakery: '4020',
  drinks: '4030', tea: '4030', sweets: '4040', misc: '4040',
}

// Map supplier categories to COGS GL codes
const SUPPLIER_COGS_MAP = {
  Meat: '5010', Bakery: '5020', Groceries: '5030',
  Beverages: '5040', Packaging: '5050',
}

export function getGLAccounts() {
  return Object.values(CHART_OF_ACCOUNTS)
}

export function getGLAccount(code) {
  return CHART_OF_ACCOUNTS[code] || null
}

// ────────────────────────────────────────────
// VAT Calculations
// ────────────────────────────────────────────

// All selling prices are VAT-inclusive in SA
export function extractVAT(inclAmount) {
  const excl = inclAmount / (1 + VAT_RATE)
  const vat = inclAmount - excl
  return { inclusive: inclAmount, exclusive: Number(excl.toFixed(2)), vat: Number(vat.toFixed(2)) }
}

export function addVAT(exclAmount) {
  const incl = exclAmount * (1 + VAT_RATE)
  return { inclusive: Number(incl.toFixed(2)), exclusive: exclAmount, vat: Number((incl - exclAmount).toFixed(2)) }
}

// ────────────────────────────────────────────
// Daily P&L Snapshot
// ────────────────────────────────────────────

export function getDailyPnL(departmentId, date) {
  const sales = getPosSales(departmentId, date, date).filter(s => !s.voided)
  const products = getAllPosProducts(departmentId)
  const wastage = getWastageLog(departmentId, date, date)
  const overheads = getOverheads(departmentId)

  // Revenue
  let totalRevenue = 0
  let totalCOGS = 0
  const revenueByGL = {}
  const productPerformance = []

  for (const sale of sales) {
    const saleTotal = sale.total || sale.items?.reduce((s, i) => s + (i.price || 0) * (i.qty || 1), 0) || 0
    totalRevenue += saleTotal

    if (sale.items) {
      for (const item of sale.items) {
        const product = products.find(p => p.id === item.productId)
        const costCard = product ? getProductCostCard(product.id) : null
        const itemCOGS = costCard ? costCard.cogs * (item.qty || 1) : 0
        totalCOGS += itemCOGS

        const glCode = product ? (DEPT_REVENUE_MAP[product.department] || '4040') : '4040'
        revenueByGL[glCode] = (revenueByGL[glCode] || 0) + (item.price || 0) * (item.qty || 1)
      }
    }
  }

  // Wastage cost
  let wastageCost = 0
  for (const w of wastage) {
    const invItem = w.inventoryItemId ? getInventoryItems(departmentId).find(i => i.id === w.inventoryItemId) : null
    wastageCost += (invItem?.lastPurchasePrice || 0) * (w.qty || 0)
  }

  // Daily overhead
  const monthlyOverhead = overheads.reduce((s, o) => s + (Number(o.amount) || 0), 0)
  const dailyOverhead = monthlyOverhead / TRADING_DAYS_PER_MONTH

  // VAT
  const revenueVAT = extractVAT(totalRevenue)
  const inputVAT = extractVAT(totalCOGS) // Simplified: assume purchases include VAT

  const grossProfit = revenueVAT.exclusive - totalCOGS
  const netProfit = grossProfit - wastageCost - dailyOverhead

  return {
    date,
    departmentId,
    revenue: {
      inclusive: totalRevenue,
      exclusive: revenueVAT.exclusive,
      outputVAT: revenueVAT.vat,
      byGL: revenueByGL,
    },
    cogs: {
      total: totalCOGS,
      wastage: wastageCost,
      inputVAT: inputVAT.vat,
    },
    overheads: {
      daily: Number(dailyOverhead.toFixed(2)),
      monthly: monthlyOverhead,
    },
    grossProfit: Number(grossProfit.toFixed(2)),
    grossMargin: totalRevenue > 0 ? Number(((grossProfit / revenueVAT.exclusive) * 100).toFixed(1)) : 0,
    netProfit: Number(netProfit.toFixed(2)),
    netMargin: totalRevenue > 0 ? Number(((netProfit / revenueVAT.exclusive) * 100).toFixed(1)) : 0,
    transactionCount: sales.length,
    voidedCount: getPosSales(departmentId, date, date).filter(s => s.voided).length,
    vatSummary: {
      outputVAT: revenueVAT.vat,
      inputVAT: inputVAT.vat,
      netVAT: Number((revenueVAT.vat - inputVAT.vat).toFixed(2)),
    },
  }
}

// ────────────────────────────────────────────
// Period P&L Statement
// ────────────────────────────────────────────

export function getPeriodPnL(departmentId, dateFrom, dateTo) {
  const sales = getPosSales(departmentId, dateFrom, dateTo).filter(s => !s.voided)
  const purchases = getPurchases(departmentId, null, dateFrom, dateTo)
  const wastage = getWastageLog(departmentId, dateFrom, dateTo)
  const overheads = getOverheads(departmentId)
  const products = getAllPosProducts(departmentId)

  // Revenue
  let totalRevenue = 0
  let totalCOGS = 0
  const revenueByDept = {}

  for (const sale of sales) {
    const saleTotal = sale.total || 0
    totalRevenue += saleTotal
    if (sale.items) {
      for (const item of sale.items) {
        const dept = item.department || 'other'
        revenueByDept[dept] = (revenueByDept[dept] || 0) + (item.price || 0) * (item.qty || 1)
        const product = products.find(p => p.id === item.productId)
        const costCard = product ? getProductCostCard(product.id) : null
        totalCOGS += costCard ? costCard.cogs * (item.qty || 1) : 0
      }
    }
  }

  // Purchases total
  const totalPurchases = purchases
    .filter(p => p.status === 'confirmed')
    .reduce((s, p) => s + (p.lineItems || []).reduce((ls, li) => ls + (Number(li.qty) || 0) * (Number(li.unitPrice) || 0), 0), 0)

  // Wastage
  const inventoryItems = getInventoryItems(departmentId)
  let wastageCost = 0
  for (const w of wastage) {
    const inv = inventoryItems.find(i => i.id === w.inventoryItemId)
    wastageCost += (inv?.lastPurchasePrice || 0) * (w.qty || 0)
  }

  // Days in period
  const d1 = new Date(dateFrom)
  const d2 = new Date(dateTo)
  const days = Math.max(1, Math.ceil((d2 - d1) / 86400000) + 1)
  const tradingDays = Math.ceil(days * (TRADING_DAYS_PER_MONTH / 30))

  // Overheads
  const monthlyOverhead = overheads.reduce((s, o) => s + (Number(o.amount) || 0), 0)
  const periodOverhead = (monthlyOverhead / TRADING_DAYS_PER_MONTH) * tradingDays

  // VAT
  const revVAT = extractVAT(totalRevenue)
  const purchVAT = extractVAT(totalPurchases)

  const grossProfit = revVAT.exclusive - totalCOGS - wastageCost
  const netProfit = grossProfit - periodOverhead

  return {
    period: { from: dateFrom, to: dateTo, days, tradingDays },
    revenue: {
      total: totalRevenue,
      exclusive: revVAT.exclusive,
      outputVAT: revVAT.vat,
      byDepartment: revenueByDept,
      avgDaily: Number((totalRevenue / tradingDays).toFixed(2)),
    },
    costOfSales: {
      cogs: Number(totalCOGS.toFixed(2)),
      wastage: Number(wastageCost.toFixed(2)),
      total: Number((totalCOGS + wastageCost).toFixed(2)),
    },
    purchases: {
      total: Number(totalPurchases.toFixed(2)),
      inputVAT: purchVAT.vat,
    },
    overheads: {
      monthly: monthlyOverhead,
      period: Number(periodOverhead.toFixed(2)),
      byItem: overheads.map(o => ({ name: o.name, amount: Number(o.amount) || 0, glCode: OVERHEAD_GL_MAP[o.name] || '6000' })),
    },
    grossProfit: Number(grossProfit.toFixed(2)),
    grossMargin: revVAT.exclusive > 0 ? Number(((grossProfit / revVAT.exclusive) * 100).toFixed(1)) : 0,
    netProfit: Number(netProfit.toFixed(2)),
    netMargin: revVAT.exclusive > 0 ? Number(((netProfit / revVAT.exclusive) * 100).toFixed(1)) : 0,
    vatReturn: {
      outputVAT: revVAT.vat,
      inputVAT: purchVAT.vat,
      netPayable: Number((revVAT.vat - purchVAT.vat).toFixed(2)),
    },
    transactions: sales.length,
  }
}

// ────────────────────────────────────────────
// Cash Flow Summary
// ────────────────────────────────────────────

export function getCashFlowSummary(departmentId, dateFrom, dateTo) {
  const sales = getPosSales(departmentId, dateFrom, dateTo).filter(s => !s.voided)
  const purchases = getPurchases(departmentId, null, dateFrom, dateTo).filter(p => p.status === 'confirmed')
  const overheads = getOverheads(departmentId)

  // Inflows
  const cashSales = sales.filter(s => s.paymentMethod === 'cash')
    .reduce((sum, s) => sum + (s.total || 0), 0)
  const cardSales = sales.filter(s => s.paymentMethod !== 'cash')
    .reduce((sum, s) => sum + (s.total || 0), 0)
  const totalInflows = cashSales + cardSales

  // Outflows
  const purchasePayments = purchases
    .reduce((sum, p) => sum + (p.lineItems || []).reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unitPrice) || 0), 0), 0)

  const d1 = new Date(dateFrom)
  const d2 = new Date(dateTo)
  const days = Math.max(1, Math.ceil((d2 - d1) / 86400000) + 1)
  const tradingDays = Math.ceil(days * (TRADING_DAYS_PER_MONTH / 30))
  const monthlyOverhead = overheads.reduce((sum, o) => sum + (Number(o.amount) || 0), 0)
  const periodOverhead = (monthlyOverhead / TRADING_DAYS_PER_MONTH) * tradingDays
  const totalOutflows = purchasePayments + periodOverhead

  return {
    period: { from: dateFrom, to: dateTo },
    inflows: {
      cashSales,
      cardSales,
      total: totalInflows,
    },
    outflows: {
      purchases: Number(purchasePayments.toFixed(2)),
      overheads: Number(periodOverhead.toFixed(2)),
      total: Number(totalOutflows.toFixed(2)),
    },
    netCashFlow: Number((totalInflows - totalOutflows).toFixed(2)),
    vatLiability: extractVAT(totalInflows).vat - extractVAT(purchasePayments).vat,
  }
}

// ────────────────────────────────────────────
// VAT Return Preparation (SARS VAT201)
// ────────────────────────────────────────────

export function prepareVAT201(departmentId, periodFrom, periodTo) {
  const pnl = getPeriodPnL(departmentId, periodFrom, periodTo)
  const branch = getBranch(departmentId)

  return {
    tradingName: branch === 'yahwehs_delight' ? "Yahweh's Delight" : 'Desres Kitchen & Confectionery',
    vatPeriod: { from: periodFrom, to: periodTo },
    // Box 1: Total value of supplies (excl VAT)
    box1_standardRatedSupplies: pnl.revenue.exclusive,
    // Box 1A: Output VAT on supplies
    box1a_outputVAT: pnl.vatReturn.outputVAT,
    // Box 14: Total value of acquisitions (excl VAT)
    box14_capitalGoods: 0,
    // Box 15: Input VAT on acquisitions
    box15_inputVAT: pnl.vatReturn.inputVAT,
    // Box 18: Net VAT payable / (refundable)
    box18_netVAT: pnl.vatReturn.netPayable,
    // Supporting data
    totalSalesInclVAT: pnl.revenue.total,
    totalPurchasesInclVAT: pnl.purchases.total,
    transactionCount: pnl.transactions,
  }
}

// ────────────────────────────────────────────
// Income Tax Preparation (IRP6 Provisional)
// ────────────────────────────────────────────

export function prepareIRP6(departmentId, taxYearStart, taxYearEnd) {
  const pnl = getPeriodPnL(departmentId, taxYearStart, taxYearEnd)

  const taxableIncome = pnl.netProfit
  // Simplified: assume sole proprietor tax brackets (2025/26 rates)
  let estimatedTax = 0
  if (taxableIncome > 0) {
    if (taxableIncome <= 237100) estimatedTax = taxableIncome * 0.18
    else if (taxableIncome <= 370500) estimatedTax = 42678 + (taxableIncome - 237100) * 0.26
    else if (taxableIncome <= 512800) estimatedTax = 77362 + (taxableIncome - 370500) * 0.31
    else if (taxableIncome <= 673000) estimatedTax = 121475 + (taxableIncome - 512800) * 0.36
    else if (taxableIncome <= 857900) estimatedTax = 179147 + (taxableIncome - 673000) * 0.39
    else if (taxableIncome <= 1817000) estimatedTax = 251258 + (taxableIncome - 857900) * 0.41
    else estimatedTax = 644489 + (taxableIncome - 1817000) * 0.45
  }

  return {
    taxYear: { from: taxYearStart, to: taxYearEnd },
    grossRevenue: pnl.revenue.exclusive,
    costOfSales: pnl.costOfSales.total,
    grossProfit: pnl.grossProfit,
    operatingExpenses: pnl.overheads.period,
    taxableIncome: Number(taxableIncome.toFixed(2)),
    estimatedTax: Number(estimatedTax.toFixed(2)),
    effectiveRate: taxableIncome > 0 ? Number(((estimatedTax / taxableIncome) * 100).toFixed(1)) : 0,
  }
}

// ────────────────────────────────────────────
// Data Completeness Score
// ────────────────────────────────────────────

export function getDataCompletenessScore(departmentId, date) {
  const sales = getPosSales(departmentId, date, date)
  const products = getAllPosProducts(departmentId)
  const inventory = getInventoryItems(departmentId)

  const checks = [
    { name: 'Sales recorded', ok: sales.length > 0 },
    { name: 'Products have prices', ok: products.every(p => p.sellingPrice > 0) },
    { name: 'Inventory levels set', ok: inventory.some(i => i.currentStock > 0) },
    { name: 'Reorder levels defined', ok: inventory.every(i => i.reorderLevel >= 0) },
    { name: 'No voided sales', ok: !sales.some(s => s.voided) },
    { name: 'Payment method recorded', ok: sales.every(s => s.paymentMethod) },
  ]

  const score = checks.filter(c => c.ok).length
  const total = checks.length

  return {
    date,
    score,
    total,
    pct: Number(((score / total) * 100).toFixed(0)),
    checks,
    grade: score === total ? 'A' : score >= total * 0.8 ? 'B' : score >= total * 0.6 ? 'C' : 'D',
  }
}

// ────────────────────────────────────────────
// Reconciliation Lock
// ────────────────────────────────────────────

function lockKey(dept) { return `desres_pos_locks_${dept}` }

export function getLockedPeriods(departmentId) {
  try {
    return JSON.parse(localStorage.getItem(lockKey(departmentId)) || '[]')
  } catch { return [] }
}

export function lockPeriod(departmentId, date, lockedBy) {
  const locks = getLockedPeriods(departmentId)
  if (locks.find(l => l.date === date)) return false
  const entry = { date, lockedBy, lockedAt: new Date().toISOString() }
  locks.push(entry)
  localStorage.setItem(lockKey(departmentId), JSON.stringify(locks))
  // Fire-and-forget Firestore write — one doc per dept per date, so
  // a lock placed on Device A prevents edits from Device B too.
  const docId = `${departmentId}_${date}`.toLowerCase()
  restSet(`periodLocks/${docId}`, { id: docId, departmentId, ...entry }).catch(err => {
    console.warn('[accounting] lockPeriod Firestore write failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'accounting', fn: 'lockPeriod', departmentId, date } })
  })
  return true
}

export function unlockPeriod(departmentId, date, unlockedBy) {
  let locks = getLockedPeriods(departmentId)
  locks = locks.filter(l => l.date !== date)
  localStorage.setItem(lockKey(departmentId), JSON.stringify(locks))
  const docId = `${departmentId}_${date}`.toLowerCase()
  restDelete(`periodLocks/${docId}`).catch(err => {
    console.warn('[accounting] unlockPeriod Firestore delete failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'accounting', fn: 'unlockPeriod', departmentId, date } })
  })
  return true
}

export function isPeriodLocked(departmentId, date) {
  return getLockedPeriods(departmentId).some(l => l.date === date)
}

/** Pull latest locks from Firestore into localStorage. Call from
 *  Accounting page mount so a lock placed by one device is visible
 *  on another. Falls back silently to local cache on failure. */
export async function refreshPeriodLocks(departmentId) {
  if (!departmentId) return
  try {
    const list = await restQuery('periodLocks', 'departmentId', departmentId)
    if (Array.isArray(list)) {
      const normalised = list.map(l => ({
        date: l.date, lockedBy: l.lockedBy, lockedAt: l.lockedAt,
      }))
      localStorage.setItem(lockKey(departmentId), JSON.stringify(normalised))
    }
  } catch (err) {
    console.warn('[accounting] refreshPeriodLocks failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'accounting', fn: 'refreshPeriodLocks', departmentId } })
  }
}

// ────────────────────────────────────────────
// Audit Trail Enhancement
// ────────────────────────────────────────────

function auditKey() { return 'desres_pos_auditTrail' }

// Audit trail entries are mirrored to the `auditTrail` Firestore
// collection so a device wipe doesn't lose the trail. localStorage
// stays the primary read path (speed + offline), Firestore the
// permanent record. IDs are deterministic per entry so re-writes
// overwrite instead of duplicating.

export function logAuditEvent(event) {
  const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
  const entry = {
    ...event,
    id,
    timestamp: new Date().toISOString(),
  }
  try {
    const trail = JSON.parse(localStorage.getItem(auditKey()) || '[]')
    trail.push(entry)
    // Keep last 5000 entries in localStorage (speed cache)
    if (trail.length > 5000) trail.splice(0, trail.length - 5000)
    localStorage.setItem(auditKey(), JSON.stringify(trail))
  } catch (err) {
    console.warn('[accounting] auditTrail localStorage write failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'accounting', fn: 'logAuditEvent.local' } })
  }
  // Fire-and-forget Firestore mirror — permanent record, survives
  // a device wipe or localStorage quota purge.
  restSet(`auditTrail/${id}`, entry).catch(err => {
    console.warn('[accounting] auditTrail Firestore mirror failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'accounting', fn: 'logAuditEvent.firestore', id } })
  })
}

export function getAuditTrail(departmentId, dateFrom, dateTo, limit = 100) {
  try {
    let trail = JSON.parse(localStorage.getItem(auditKey()) || '[]')
    if (departmentId) trail = trail.filter(e => e.departmentId === departmentId)
    if (dateFrom) trail = trail.filter(e => e.timestamp >= dateFrom)
    if (dateTo) trail = trail.filter(e => e.timestamp <= dateTo + 'T23:59:59')
    return trail.sort((a, b) => b.timestamp.localeCompare(a.timestamp)).slice(0, limit)
  } catch { return [] }
}

/** Pull the last 30 days of audit entries from Firestore into the
 *  localStorage cache. Called from Accounting.jsx mount so a fresh
 *  device load can see the cross-device audit trail, not just the
 *  local history. Falls back silently to whatever's cached locally. */
export async function refreshAuditTrail(departmentId) {
  if (!departmentId) return
  try {
    const all = await restQuery('auditTrail', 'departmentId', departmentId)
    if (!Array.isArray(all)) return
    // 30-day window — keeps the cache small (matches the old 5000
    // cap in spirit but age-based rather than size-based).
    const cutoffMs = Date.now() - 30 * 24 * 60 * 60 * 1000
    const fresh = all.filter(e => {
      try { return new Date(e.timestamp).getTime() >= cutoffMs }
      catch { return true }
    })
    // Merge with local cache to preserve recent entries that haven't
    // round-tripped yet (in case Firestore query is a hair behind).
    let local = []
    try { local = JSON.parse(localStorage.getItem(auditKey()) || '[]') }
    catch { local = [] }
    const seen = new Set(fresh.map(e => e.id))
    for (const e of local) { if (!seen.has(e.id)) fresh.push(e) }
    fresh.sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''))
    if (fresh.length > 5000) fresh.splice(0, fresh.length - 5000)
    localStorage.setItem(auditKey(), JSON.stringify(fresh))
  } catch (err) {
    console.warn('[accounting] refreshAuditTrail failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'accounting', fn: 'refreshAuditTrail', departmentId } })
  }
}

// ────────────────────────────────────────────
// Stock Valuation (Weighted Average Cost)
// ────────────────────────────────────────────

export function getStockValuation(departmentId) {
  const items = getInventoryItems(departmentId)
  let totalValue = 0
  const itemValues = []

  for (const item of items) {
    const value = (item.currentStock || 0) * (item.lastPurchasePrice || item.avgCost || 0)
    totalValue += value
    itemValues.push({
      id: item.id,
      name: item.name,
      qty: item.currentStock || 0,
      unit: item.unit,
      unitCost: item.lastPurchasePrice || item.avgCost || 0,
      totalValue: Number(value.toFixed(2)),
    })
  }

  return {
    departmentId,
    date: new Date().toISOString().split('T')[0],
    items: itemValues.sort((a, b) => b.totalValue - a.totalValue),
    totalValue: Number(totalValue.toFixed(2)),
    method: 'weighted_average',
  }
}

// ────────────────────────────────────────────
// Break-even Analysis
// ────────────────────────────────────────────

export function getBreakevenAnalysis(departmentId) {
  const products = getAllPosProducts(departmentId)
  const overheads = getOverheads(departmentId)
  const monthlyFixed = overheads.filter(o => o.category === 'fixed').reduce((s, o) => s + (Number(o.amount) || 0), 0)
  const monthlyVariable = overheads.filter(o => o.category === 'variable').reduce((s, o) => s + (Number(o.amount) || 0), 0)
  const dailyFixed = monthlyFixed / TRADING_DAYS_PER_MONTH

  // Average contribution margin across all products
  let totalCM = 0, count = 0
  for (const p of products) {
    const card = getProductCostCard(p.id)
    if (card && card.sellingPrice > 0) {
      totalCM += (card.grossProfit / card.sellingPrice)
      count++
    }
  }
  const avgCMRatio = count > 0 ? totalCM / count : 0.5

  const dailyBreakeven = avgCMRatio > 0 ? dailyFixed / avgCMRatio : 0
  const monthlyBreakeven = dailyBreakeven * TRADING_DAYS_PER_MONTH

  return {
    departmentId,
    fixedCosts: { monthly: monthlyFixed, daily: Number(dailyFixed.toFixed(2)) },
    variableCosts: { monthly: monthlyVariable },
    avgContributionMarginRatio: Number((avgCMRatio * 100).toFixed(1)),
    breakeven: {
      daily: Number(dailyBreakeven.toFixed(2)),
      monthly: Number(monthlyBreakeven.toFixed(2)),
    },
    productCount: count,
  }
}
